<template>

  <template v-if="initialDataIsLoading">
    <i class="fa fa-spinner fa-spin fa-2x"></i>
  </template>

  <template v-else>
    <!-- GA enabled mode -->
    <template v-if="enabled">
      <button class="btn btn-primary btn-sm" type="button" @click="openModal('edit')" :disabled="actionIsBlocked">Edit</button>
    </template>

      <!-- GA disabled mode -->
    <template v-else>
      <button class="btn btn-success btn-sm" type="button" @click="openModal('setup')" :disabled="actionIsBlocked">Set up</button>
    </template>
  </template>

  <!-- GA setup modal -->
  <modal id="gaSetupModal" title="Google Authenticator Setup" @hidden="onCloseModal()">

    <!-- Modal window: if data is loading -->
    <template v-if="dataIsLoading">
      <div class="text-center">
        <i class="fa fa-spinner fa-spin fa-5x"></i>
      </div>
    </template>

    <!-- Modal window: if data is loaded -->
    <template v-else>

      <!-- Modal window mode: edit -->
      <template v-if="modalMode === 'edit'">
        <div class="text-center">

          <button class="btn btn-primary" type="button" @click="modalMode = 'setup'">Re-configure Google Authenticator</button>
        </div>
      </template>
        <!-- End of modal window mode: edit -->

        <!-- Modal window mode: setup -->
      <template v-if="modalMode === 'setup'">

        <!-- Setup first screen: QR code -->
        <template v-if="setupMode === 'qr'">
          <ul>
            <li>
              Get the Authenticator App from the
              <a href="https://apps.apple.com/ru/app/google-authenticator/id388497605" target="_blank">App Store</a> or
              <a href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2" target="_blank">Google Play</a>.
            </li>
            <li>In the App select <strong>Set up account</strong>.</li>
            <li>Choose <strong>Scan barcode</strong>.</li>
          </ul>

          <div class="text-center">
            <img :src="qr" alt="GA QR code"><br>
            <a href="#" @click="setupMode = 'key'">CAN'T SCAN IT?</a>
          </div>
        </template>

        <!-- Setup alternative screen: key -->
        <template v-if="setupMode === 'key'">
          <h3 class="text-center">Can't scan the barcode?</h3>

          <ol>
            <li>Tap <strong>Menu</strong>, then <strong>Set up account</strong>.</li>
            <li>Tap <strong>Enter provided key</strong>.</li>
            <li>
              Enter your email address and this key:
              <div class="secret-code-block">
                <strong class="secret-key">{{ newSecretEasyToRead }}</strong>
                <div class="m-t-md text-muted">spaces don't matter</div>
              </div>
            </li>
            <li>Make sure <strong>Time based</strong> is turned on, and tap <strong>Add</strong> to finish.</li>
          </ol>
        </template>

        <!-- Setup alternative screen: verify -->
        <template v-if="setupMode === 'verify'">
          <div v-if="error">
            <div class="alert alert-danger text-center">
              {{ error }}
            </div>
          </div>

          <div class="form-group row">
            <label for="code" class="col-sm-5 col-form-label">Enter the 6-digit code you see in the app:</label>
            <div class="col-sm-7">
              <input type="text" class="form-control" id="code" v-model="fields.code">
              <div v-if="errors && errors.code" class="text-danger">{{ errors.code[0] }}</div>
            </div>
          </div>
        </template>

      </template>
      <!-- End of modal window mode: setup -->
    </template>

    <template #footer>
      <button type="button" class="btn btn-default" @click="closeModal()">Close</button>

      <template v-if="modalMode === 'edit'">
        <button class="btn btn-danger" type="button" @click="confirmDisable()" :disabled="disablingIsProcessing">
          <i v-if="disablingIsProcessing" class="fa fa-fw fa-spinner fa-spin"></i>
          Disable
        </button>
      </template>

      <template v-if="modalMode === 'setup'">
        <button v-if="setupMode !== 'qr'" type="button" class="btn btn-default" @click="setupMode = 'qr'">Back</button>
        <button v-if="setupMode !== 'verify'" type="button" class="btn btn-default" @click="setupMode = 'verify'">Next</button>

        <button v-if="setupMode === 'verify'" type="submit" class="btn btn-primary" @click="submit()" :disabled="formIsProcessing">
          <i v-if="!formIsProcessing" class="fa fa-fw fa-check"></i>
          <i v-if="formIsProcessing" class="fa fa-fw fa-spinner fa-spin"></i>
          Verify and enable
        </button>
      </template>
    </template>
  </modal>

</template>

<script>
export default {

  /**
   * Input properties
   */
  props: {

    // User's ID.
    userId: {
      type: String,
      default: "",
    },

    // The url to request fresh GA configuration data.
    dataUrl: {
      type: String,
      default: "",
    },

  },

  /**
   * Variables (models).
   */
  data () {
    return {

      // GA initial, currently saved secret key.
      savedSecret: '',

      // New setup secret key.
      newSecret: '',

      // New setup QR code URL.
      qr: '',

      // Current modal window mode (one of "setup", "edit").
      modalMode: "setup",

      // Current GA setup screen (one of "qr", "key", "verify").
      setupMode: "qr",

      // Verification form fields
      fields: {},

      // Form validation error messages
      errors: {},

      // Internal validation state
      isValid: true,

      // Common error message
      error: '',

      // Disable button click is processing now indicator.
      disablingIsProcessing: false,

      // Loading indicators.
      formIsProcessing: false,
      dataIsLoading: false,
      initialDataIsLoading: false,

      // Is main button action disabled.
      actionIsBlocked: false,
    }
  },

  /**
   * Computed values.
   */
  computed: {

    // GA enabled.
    enabled: function () {
      return this.savedSecret !== "";
    },

    // Secret key in easy to read format.
    newSecretEasyToRead: function () {
      return this.newSecret.toLowerCase().match(/.{1,4}/g).join(' ');
    },

  },

  /**
   * Functions (methods).
   */
  methods: {

    /**
     * Init component.
     */
    init() {
      this.clearForm();
      this.loadFreshConfiguration(true);
    },

    /**
     * Load fresh authenticator configuration.
     */
    loadFreshConfiguration(isInitial = false) {
      if (isInitial) {
        this.initialDataIsLoading = true;
      } else {
        this.dataIsLoading = true;
      }

      axios.get(this.dataUrl)
        .then(response => {
          this.clearForm();
          this.savedSecret = response.data.saved_secret;
          this.newSecret = response.data.new_secret;
          this.qr = response.data.qr;
        })
        .finally(() => {
          if (isInitial) {
            this.initialDataIsLoading = false;
          } else {
            this.dataIsLoading = false;
          }
        });
    },

    /**
     * Submit verification form.
     */
    submit() {
      this.validate();
      if (!this.isValid) return;
      this.enable();
    },

    /**
     * Validate form
     */
    validate() {
      this.errors = {};
      this.isValid = true;

      // Validate verification code.
      if (!this.fields.code) {
        this.setError('code', 'Verification code is required.');
      }
    },

    /**
     * Prepare form data
     */
    formData() {
      const formData = new FormData();
      Object.keys(this.fields).forEach(fieldName => {
        const value = this.fields[fieldName] ? this.fields[fieldName] : '';
        formData.append(fieldName, value);
      });
      formData.append('secret', this.newSecret);
      return formData;
    },

    /**
     * Enable GA.
     */
    enable() {
      let formData = this.formData();
      this.formIsProcessing = true;
      axios.post('/user/' + this.userId + '/2fa/config/ga/enable', formData)
          .then(response => {
            this.clearForm();
            this.actionIsBlocked = true;
            location.reload();
            this.closeModal();
          })
          .catch(error => {
            this.handleErrors(error, 'Unable to verify code')
          })
          .finally(() => {
            this.formIsProcessing = false;
          });
    },

    /**
     * Disable GA.
     */
    disable() {
      this.disablingIsProcessing = true;
      axios.post('/user/' + this.userId + '/2fa/config/ga/disable')
          .then(response => {
            this.actionIsBlocked = true;
            location.reload();
            this.closeModal();
          })
          .catch(error => {
            this.handleErrors(error, 'Unable to process request.')
          })
          .finally(() => {
            this.disablingIsProcessing = false;
          });
    },

    /**
     * Disable dialog.
     */
    confirmDisable() {
      let text = 'You are about to disable Google Authenticator 2FA method for your profile. ';
      text += 'This action cannot be undone. ';

      this.$swal.fire({
        title: 'Disable Google Authenticator',
        text: text,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Proceed'
      }).then((result) => {
        if (result.value) {
          this.disable();
        }
      })
    },

    /**
     * Open GA setup modal.
     */
    openModal(mode) {
      this.clearForm();
      this.modalMode = mode;
      this.setupMode = 'qr';
      this.$modal.show('gaSetupModal');
    },

    /**
     * Close GA setup modal.
     */
    closeModal() {
      this.$modal.hide('gaSetupModal');
    },

    /**
     * Event handler.
     */
    onCloseModal() {
      this.loadFreshConfiguration();
    },

    /**
     * Set validation error
     */
    setError(field, message) {
      if (!this.errors[field]) this.errors[field] = [];
      this.errors[field].push(message);
      this.isValid = false;
    },

    /**
     * Handle axios errors
     *
     * @param errorObj
     * @param defaultMessage
     */
    handleErrors(errorObj, defaultMessage) {
      if (errorObj.response.status === 422) {
        this.errors = errorObj.response.data.errors || {};
      } else {
        if (errorObj.response.status === 403) {
          this.error = defaultMessage + ': access denied.';
        } else {
          this.error = defaultMessage;
        }
      }
    },

    /**
     * Clear form fields and messages
     */
    clearForm() {
      this.fields = {};
      this.errors = {};
      this.error = '';
    }

  },

  /**
   * Initialize component on mount.
   */
  mounted () {
    this.init();
  }

}
</script>

<style scoped>
.secret-code-block {
  border: 1px dotted grey;
  padding: 1em;
  text-align: center;
}
.secret-code-block > .secret-key {
  font-size: 2em;
}
</style>
